import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosDelete } from '../api/axios';
import { toast } from 'react-toastify';

export const audioSamplesQueryKeys = {
  all: ['audioSamples'] as const,
  list: (params?: {
    page?: number;
    limit?: number;
    vertical_id?: number | null;
    experience_type?: 'audio' | 'video' | null;
  }) => [...audioSamplesQueryKeys.all, 'list', params] as const,
  byVertical: (params?: { vertical_id: number; page?: number; limit?: number }) =>
    [...audioSamplesQueryKeys.all, 'vertical', params] as const,
};

export interface AudioSampleVerticalSummary {
  id: number;
  key: string;
  name: string;
}

export interface AudioSampleListenerAudioSummary {
  id: number;
  title: string | null;
  listener_role_id: number | null;
  display_name?: string | null;
  profile_photo_square_file_url?: string | null;
  file_url?: string | null;
  duration?: number | null;
  tag_names?: string[];
  topic_tag_ids?: number[];
  media_type?: string | null;
  video_id?: string | null;
}

export interface AudioSample {
  id: number;
  vertical_id: number;
  listener_audio_id: number;
  created_at: string;
  vertical?: AudioSampleVerticalSummary | null;
  listener_audio?: AudioSampleListenerAudioSummary | null;
}

export interface AudioSampleListResponse {
  count: number;
  data: AudioSample[];
}

export interface AudioSamplesByVerticalResponse extends AudioSampleListResponse {
  vertical_id: number | null;
}

export interface AudioSampleCreatePayload {
  vertical_id: number;
  listener_audio_id: number;
}

const getAudioSamplesList = async (params?: {
  page?: number;
  limit?: number;
  vertical_id?: number | null;
  experience_type?: 'audio' | 'video' | null;
}): Promise<AudioSampleListResponse> => {
  const { page = 1, limit = 20, vertical_id, experience_type } = params ?? {};
  const query: Record<string, string | number> = { page, limit };
  if (vertical_id != null) {
    query.vertical_id = vertical_id;
  }
  if (experience_type != null) {
    query.experience_type = experience_type;
  }
  const response = await axiosGet('/audio_samples/', query, 'v3');
  return response.data as AudioSampleListResponse;
};

export const useAudioSamplesList = (
  params?: {
    page?: number;
    limit?: number;
    vertical_id?: number | null;
    experience_type?: 'audio' | 'video' | null;
  },
  options?: { enabled?: boolean },
) => {
  return useQuery<AudioSampleListResponse>({
    queryKey: audioSamplesQueryKeys.list(params),
    queryFn: () => getAudioSamplesList(params),
    enabled: options?.enabled ?? true,
  });
};

const getAudioSamplesByVertical = async (params: {
  vertical_id: number;
  page?: number;
  limit?: number;
}): Promise<AudioSamplesByVerticalResponse> => {
  const { vertical_id, page = 1, limit = 20 } = params;
  const response = await axiosGet(`/audio_samples/vertical/${vertical_id}`, { page, limit }, 'v3');
  return response.data as AudioSamplesByVerticalResponse;
};

export const useAudioSamplesByVertical = (
  params: { vertical_id: number; page?: number; limit?: number },
  options?: { enabled?: boolean },
) => {
  return useQuery<AudioSamplesByVerticalResponse>({
    queryKey: audioSamplesQueryKeys.byVertical(params),
    queryFn: () => getAudioSamplesByVertical(params),
    enabled: (options?.enabled ?? true) && params.vertical_id != null,
  });
};

const createAudioSample = async (payload: AudioSampleCreatePayload): Promise<AudioSample> => {
  const response = await axiosPost('/audio_samples/', payload, 'v3');
  return response.data as AudioSample;
};

export const useCreateAudioSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createAudioSample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: audioSamplesQueryKeys.all });
      toast.success('Audio sample added.');
    },
    onError: (error: { response?: { data?: { description?: string } } }) => {
      const message = error?.response?.data?.description ?? 'Failed to add audio sample.';
      toast.error(message);
    },
  });
};

const deleteAudioSample = async (id: number): Promise<void> => {
  await axiosDelete(`/audio_samples/${id}`, undefined, 'v3');
};

export const useDeleteAudioSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteAudioSample,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: audioSamplesQueryKeys.all });
      toast.success('Audio sample removed.');
    },
    onError: (error: { response?: { data?: { description?: string } } }) => {
      const message = error?.response?.data?.description ?? 'Failed to remove audio sample.';
      toast.error(message);
    },
  });
};
